import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Grid,
  Box,
  Button,
  Paper,
  ClickAwayListener,
  Grow,
  Popper
} from "@mui/material";
import styled from "@emotion/styled";
import LoyaltyCardContainer from "./LoyaltyCard";
import axios from "axios";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { enUS } from "date-fns/locale";
import { styled as muiStyled } from "@mui/material/styles";

const DateRangePickerContainer = DateRangePicker as any;

const MetricsContainer = styled(Box)({
  marginTop: "20px"
});

const PeriodSelector = styled(Box)({
  marginBottom: "20px",
  display: "flex",
  justifyContent: "flex-end"
});

const StyledButton = styled(Button)({
  borderRadius: "20px",
  border: "1px solid #192f67",
  color: "#192f67",
  textTransform: "none",
  fontWeight: 500,
  fontSize: "12px",
  padding: "6px 16px",
  minWidth: "80px",
  height: "32px",
  "&.MuiButton-contained": {
    backgroundColor: "#f0f2f7",
    borderColor: "#192f67",
    color: "#192f67",
    "&:hover": {
      backgroundColor: "#e0e4ed"
    }
  },
  "&.MuiButton-outlined": {
    backgroundColor: "white",
    borderColor: "#192f67",
    color: "#192f67",
    "&:hover": {
      backgroundColor: "#f8f9fa",
      borderColor: "#0f1f4a"
    }
  }
});

// Compact styling for react-date-range to remove leftover whitespace
const PickerWrapper = muiStyled("div")({
  ".rdrDefinedRangesWrapper": { display: "none" },
  ".rdrDateRangeWrapper": { gap: 0 },
  ".rdrCalendarWrapper": {
    background: "#ffffff",
    borderRadius: 8
  },
  ".rdrMonthAndYearWrapper": {
    padding: "8px 8px 0 8px"
  },
  ".rdrMonthsVertical": {
    padding: 8
  },
  ".rdrMonth": { width: "auto" },
  ".rdrDateDisplayWrapper": {
    background: "transparent",
    padding: "8px 8px 8px 8px",
    borderBottom: "1px solid #eee"
  },
  ".rdrDateDisplayItem": {
    padding: "4px 8px",
    margin: "0 4px"
  },
  ".rdrDateDisplayItemActive": {
    borderColor: "#192f67"
  }
});

interface LoyaltyMetricsProps {
  businessId: string;
  businessToken: string;
}

interface MetricsData {
  members: {
    total: number;
    period: number;
    loading: boolean;
    errorMessage?: string;
  };
  points: {
    total: number;
    period: number;
    loading: boolean;
    errorMessage?: string;
  };
  checkins: {
    total: number;
    period: number;
    loading: boolean;
    errorMessage?: string;
  };
  rewards: {
    total: number;
    period: number;
    loading: boolean;
    errorMessage?: string;
  };
}

const LoyaltyMetrics: React.FC<LoyaltyMetricsProps> = ({
  businessId,
  businessToken
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState("7days");
  const [customDateRange, setCustomDateRange] = useState("");
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const customButtonRef = useRef<HTMLButtonElement>(null);
  const [dateRange, setDateRange] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: "selection"
    }
  ]);
  const [metricsData, setMetricsData] = useState<MetricsData>({
    members: { total: 0, period: 0, loading: true },
    points: { total: 0, period: 0, loading: true },
    checkins: { total: 0, period: 0, loading: true },
    rewards: { total: 0, period: 0, loading: true }
  });

  // Identify genuine timeout-style failures so that
  // the UI can show a clear red message in the metric cards.
  const isTimeoutError = (error: any): boolean => {
    const status = error?.response?.status;

    return (
      // Explicit HTTP gateway timeout
      status === 504 ||
      // Axios timeout
      error?.code === "ECONNABORTED" ||
      // Generic timeout wording
      error?.message?.toString?.().toLowerCase?.().includes("timeout") ||
      // Network error where request was made but no response received
      (!error?.response && !!error?.request)
    );
  };

  const TIMEOUT_ERROR_MESSAGE = "Data unavailable - too much data to load";

  // removed unused getPeriodValue

  const getDaysValue = (period: string) => {
    switch (period) {
      case "7days":
        return "7";
      case "28days":
        return "28";
      default:
        return "7";
    }
  };

  const fetchAllDataForPeriod = useCallback(
    async (period: string, customDateRange?: string) => {
      let queryType: string;
      let queryValue: string;

      if (period === "custom" && customDateRange) {
        // For custom dates: type=date&value=startDate,endDate
        queryType = "date";
        queryValue = customDateRange;
      } else {
        // For 7/28 days: type=days&value=7 or type=days&value=28
        queryType = "days";
        queryValue = getDaysValue(period);
      }

      // Set loading for all metrics
      setMetricsData((prev) => ({
        ...prev,
        members: { ...prev.members, loading: true, errorMessage: undefined },
        points: { ...prev.points, loading: true, errorMessage: undefined },
        checkins: { ...prev.checkins, loading: true, errorMessage: undefined },
        rewards: { ...prev.rewards, loading: true, errorMessage: undefined }
      }));

      // Call all APIs in parallel - each will succeed or fail independently
      const [
        membersResult,
        pointsResult,
        rewardsResult,
        totalResult
      ] = await Promise.allSettled([
        axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/loyaltyprogram/businessportal/dashboard/members/${businessId}?type=${queryType}&value=${queryValue}`,
          { headers: { Authorization: `Bearer ${businessToken}` } }
        ),
        axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/loyaltyprogram/businessportal/dashboard/points/${businessId}?type=${queryType}&value=${queryValue}`,
          { headers: { Authorization: `Bearer ${businessToken}` } }
        ),
        axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/loyaltyprogram/businessportal/dashboard/rewardsredeemed/${businessId}?type=${queryType}&value=${queryValue}`,
          { headers: { Authorization: `Bearer ${businessToken}` } }
        ),
        axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/loyaltyprogram/businessportal/dashboard/points/all/${businessId}`,
          { headers: { Authorization: `Bearer ${businessToken}` } }
        )
      ]);

      // Update metrics independently - each API result is handled separately
      setMetricsData((prev) => {
        const updated = { ...prev };

        // Handle Members API response
        if (membersResult.status === "fulfilled") {
          updated.members = {
            total: membersResult.value.data.TotalMemberships || prev.members.total,
            period: membersResult.value.data.MembershipsForDateRange || 0,
            loading: false,
            errorMessage: undefined
          };
        } else {
          const isTimeout = isTimeoutError(membersResult.reason);

          if (isTimeout) {
            console.warn("[Dashboard] Members API timeout", {
              businessId,
              status: membersResult.reason?.response?.status,
              message: membersResult.reason?.message
            });
          } else {
            console.error("[Dashboard] Error fetching members data", {
              businessId,
              error: membersResult.reason
            });
          }

          updated.members = {
            ...prev.members,
            loading: false,
            errorMessage: isTimeout ? TIMEOUT_ERROR_MESSAGE : undefined
          };
        }

        // Handle Points - needs data from both pointsResult (period) and totalResult (total)
        const pointsPeriod = pointsResult.status === "fulfilled" 
          ? pointsResult.value.data.Points || 0 
          : prev.points.period;
        const pointsTotal = totalResult.status === "fulfilled"
          ? totalResult.value.data.TotalPoints || prev.points.total
          : prev.points.total;
        
        const pointsTimeout = 
          (pointsResult.status === "rejected" && isTimeoutError(pointsResult.reason)) ||
          (totalResult.status === "rejected" && isTimeoutError(totalResult.reason));

        if (pointsTimeout) {
          console.warn("[Dashboard] Points API timeout", {
            businessId,
            period,
            customDateRange,
            pointsStatus: pointsResult.status === "rejected" ? pointsResult.reason?.response?.status : "ok",
            totalStatus: totalResult.status === "rejected" ? totalResult.reason?.response?.status : "ok"
          });
        } else {
          if (pointsResult.status === "rejected") {
            console.error("[Dashboard] Error fetching points period data", {
              businessId,
              period,
              customDateRange,
              error: pointsResult.reason
            });
          }
          if (totalResult.status === "rejected") {
            console.error("[Dashboard] Error fetching total points data", {
              businessId,
              error: totalResult.reason
            });
          }
        }
        
        updated.points = {
          total: pointsTotal,
          period: pointsPeriod,
          loading: false,
          errorMessage: pointsTimeout ? TIMEOUT_ERROR_MESSAGE : undefined
        };

        // Handle Checkins - needs data from both pointsResult (period) and totalResult (total)
        const checkinsPeriod = pointsResult.status === "fulfilled"
          ? pointsResult.value.data.Checkins || 0
          : prev.checkins.period;
        const checkinsTotal = totalResult.status === "fulfilled"
          ? totalResult.value.data.TotalCheckins || prev.checkins.total
          : prev.checkins.total;
        
        const checkinsTimeout = 
          (pointsResult.status === "rejected" && isTimeoutError(pointsResult.reason)) ||
          (totalResult.status === "rejected" && isTimeoutError(totalResult.reason));

        if (checkinsTimeout) {
          console.warn("[Dashboard] Checkins API timeout (derived from points APIs)", {
            businessId,
            period,
            customDateRange,
            pointsStatus: pointsResult.status === "rejected" ? pointsResult.reason?.response?.status : "ok",
            totalStatus: totalResult.status === "rejected" ? totalResult.reason?.response?.status : "ok"
          });
        }
        
        updated.checkins = {
          total: checkinsTotal,
          period: checkinsPeriod,
          loading: false,
          errorMessage: checkinsTimeout ? TIMEOUT_ERROR_MESSAGE : undefined
        };

        if (pointsResult.status === "rejected" || totalResult.status === "rejected") {
          // Error already logged above
        }

        // Handle Rewards API response
        if (rewardsResult.status === "fulfilled") {
          updated.rewards = {
            total: rewardsResult.value.data.TotalRewardsRedeemed || prev.rewards.total,
            period: rewardsResult.value.data.RewardsRedeemed || 0,
            loading: false,
            errorMessage: undefined
          };
        } else {
          const isTimeout = isTimeoutError(rewardsResult.reason);

          if (isTimeout) {
            console.warn("[Dashboard] Rewards API timeout", {
              businessId,
              period,
              customDateRange,
              status: rewardsResult.reason?.response?.status,
              message: rewardsResult.reason?.message
            });
          } else {
            console.error("[Dashboard] Error fetching rewards data", {
              businessId,
              error: rewardsResult.reason
            });
          }

          updated.rewards = {
            ...prev.rewards,
            loading: false,
            errorMessage: isTimeout ? TIMEOUT_ERROR_MESSAGE : undefined
          };
        }

        return updated;
      });
    },
    [businessId, businessToken]
  );

  const handlePeriodChange = (period: string) => {
    setSelectedPeriod(period);
    setCustomDateRange(""); // Clear custom date range
    fetchAllDataForPeriod(period);
  };

  const handleCustomClick = () => {
    setIsDatePickerOpen(true);
  };

  const handleCustomDateCancel = () => {
    setIsDatePickerOpen(false);
  };

  const handleCustomDateSubmit = () => {
    if (dateRange[0]?.startDate && dateRange[0]?.endDate) {
      // Fix timezone issue by using local date formatting
      const startDate = new Date(dateRange[0].startDate);
      const endDate = new Date(dateRange[0].endDate);

      // Format dates as YYYY-MM-DD using local timezone
      const startDateString =
        startDate.getFullYear() +
        "-" +
        String(startDate.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(startDate.getDate()).padStart(2, "0");

      const endDateString =
        endDate.getFullYear() +
        "-" +
        String(endDate.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(endDate.getDate()).padStart(2, "0");

      const dateRangeString = `${startDateString},${endDateString}`;

      setCustomDateRange(dateRangeString);
      setSelectedPeriod("custom");
      setIsDatePickerOpen(false);
      fetchAllDataForPeriod("custom", dateRangeString);
    }
  };

  const getPeriodDisplayText = () => {
    if (selectedPeriod === "custom" && customDateRange) {
      return formatCustomDateRange(customDateRange);
    }
    return selectedPeriod === "7days" ? "7 days" : "28 days";
  };

  const formatCustomDateRange = (dateRange: string) => {
    if (!dateRange) return "";

    const dates = dateRange.split(",");
    if (dates.length !== 2) return "";

    const formatDate = (dateStr: string) => {
      const [year, month, day] = dateStr.split("-").map(Number);
      const date = new Date(year, month - 1, day);

      const dayFormatted = date.getDate().toString().padStart(2, "0");
      const monthFormatted = date.toLocaleDateString("en-US", {
        month: "short"
      });
      const yearFormatted = date.getFullYear().toString().slice(-2);

      return `${dayFormatted}-${monthFormatted}-${yearFormatted}`;
    };

    const startDate = formatDate(dates[0]);
    const endDate = formatDate(dates[1]);

    return `${startDate} - ${endDate}`;
  };

  useEffect(() => {
    if (businessId && businessToken) {
      fetchAllDataForPeriod("7days");
    }
  }, [businessId, businessToken, fetchAllDataForPeriod]);

  return (
    <MetricsContainer>
      <PeriodSelector>
        <Box sx={{ display: "flex", gap: "16px" }}>
          <StyledButton
            variant={selectedPeriod === "7days" ? "contained" : "outlined"}
            onClick={() => handlePeriodChange("7days")}
          >
            7 DAYS
          </StyledButton>
          <StyledButton
            variant={selectedPeriod === "28days" ? "contained" : "outlined"}
            onClick={() => handlePeriodChange("28days")}
          >
            28 DAYS
          </StyledButton>
          <StyledButton
            variant={selectedPeriod === "custom" ? "contained" : "outlined"}
            onClick={handleCustomClick}
            ref={customButtonRef}
          >
            CUSTOM
          </StyledButton>
        </Box>
      </PeriodSelector>

      {/* Date Range Picker Popper */}
      <Popper
        open={isDatePickerOpen}
        anchorEl={customButtonRef.current}
        placement="bottom-start"
        transition
        style={{ zIndex: 1300 }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper>
              <ClickAwayListener onClickAway={handleCustomDateCancel}>
                <Box sx={{ p: 0 }}>
                  <PickerWrapper>
                    <DateRangePickerContainer
                      ranges={dateRange}
                      onChange={(ranges: any) => {
                        // Accept both shapes: array or { selection }
                        const nextRanges = Array.isArray(ranges)
                          ? ranges
                          : ranges?.selection
                          ? [ranges.selection]
                          : null;
                        if (
                          nextRanges &&
                          nextRanges[0]?.startDate &&
                          nextRanges[0]?.endDate
                        ) {
                          setDateRange(nextRanges);
                        }
                      }}
                      months={1}
                      direction="vertical"
                      moveRangeOnFirstSelection={false}
                      rangeColors={["#192f67"]}
                      showSelectionPreview={true}
                      showMonthAndYearPickers={true}
                      showDateDisplay={true}
                      locale={enUS}
                      scroll={{ enabled: false }}
                      staticRanges={[]}
                      inputRanges={[]}
                    />
                  </PickerWrapper>
                  <Box
                    sx={{
                      mb: 1,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 1,
                      px: 2,
                      pb: 2
                    }}
                  >
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        size="small"
                        onClick={handleCustomDateCancel}
                        variant="outlined"
                        sx={{
                          borderColor: "#192f67",
                          color: "#192f67",
                          "&:hover": {
                            borderColor: "#0f1f4a",
                            backgroundColor: "#f0f2f7"
                          }
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        onClick={() => {
                          setDateRange([
                            {
                              startDate: new Date(),
                              endDate: new Date(),
                              key: "selection"
                            }
                          ]);
                        }}
                        variant="outlined"
                        sx={{
                          borderColor: "#192f67",
                          color: "#192f67",
                          "&:hover": {
                            borderColor: "#0f1f4a",
                            backgroundColor: "#f0f2f7"
                          }
                        }}
                      >
                        Reset
                      </Button>
                    </Box>
                    <Button
                      size="small"
                      onClick={handleCustomDateSubmit}
                      variant="contained"
                      sx={{
                        backgroundColor: "#192f67",
                        "&:hover": { backgroundColor: "#0f1f4a" }
                      }}
                    >
                      Apply
                    </Button>
                  </Box>
                </Box>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <LoyaltyCardContainer
            title="TOTAL MEMBERS"
            totalNumber={metricsData.members.total}
            isVisibleDropDownButton={false}
            loading={metricsData.members.loading}
            periodData={{
              value: metricsData.members.period,
              period: `joined (${getPeriodDisplayText()})`
            }}
            errorMessage={metricsData.members.errorMessage}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <LoyaltyCardContainer
            title="CHECK-INS"
            totalNumber={metricsData.checkins.total}
            isVisibleDropDownButton={false}
            loading={metricsData.checkins.loading}
            periodData={{
              value: metricsData.checkins.period,
              period: `check-ins (${getPeriodDisplayText()})`
            }}
            errorMessage={metricsData.checkins.errorMessage}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <LoyaltyCardContainer
            title="POINTS ISSUED"
            totalNumber={metricsData.points.total}
            isVisibleDropDownButton={false}
            loading={metricsData.points.loading}
            periodData={{
              value: metricsData.points.period,
              period: `points (${getPeriodDisplayText()})`
            }}
            errorMessage={metricsData.points.errorMessage}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <LoyaltyCardContainer
            title="REWARDS REDEEMED"
            totalNumber={metricsData.rewards.total}
            isVisibleDropDownButton={false}
            loading={metricsData.rewards.loading}
            periodData={{
              value: metricsData.rewards.period,
              period: `rewards (${getPeriodDisplayText()})`
            }}
            errorMessage={metricsData.rewards.errorMessage}
          />
        </Grid>
      </Grid>
    </MetricsContainer>
  );
};

export default LoyaltyMetrics;
